import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import type { SkillSuggestion } from '@/modules/jobs/signal/types';
import { toBand } from '@/modules/jobs/signal/verdict';

/** Server results are worthless below this length; also avoids a request per single keystroke. */
const MIN_QUERY = 2;

/**
 * Autocomplete query for `/ai/skill-search`. The caller passes the already-debounced query (see `SkillTypeahead`);
 * React Query's query-key cache + `keepPreviousData` give dedup and latest-wins, so no manual AbortController /
 * stale-response guard is needed. Disabled below {@link MIN_QUERY} chars. Wire `band` (a bare `string`) is narrowed
 * to a {@link SkillSuggestion['band']} here so consumers never handle the raw string.
 */
export const useSkillSearchQuery = (query: string, limit?: number) => {
  const trimmed = query.trim();

  return useQuery({
    queryKey: ['ai', 'skill-search', trimmed, limit ?? null] as const,
    queryFn: async (): Promise<SkillSuggestion[]> => {
      const { data } = await fetchClient.POST('/ai/skill-search', {
        body: { query: trimmed, limit },
      });
      return (data ?? []).map((item) => ({
        canonical: item.canonical,
        band: toBand(item.band),
        // `|| null` (not `??`): an empty served string is "no casing", so it collapses to null and every
        // consumer falls back to the canonical — never renders a blank label.
        display: item.display || null,
      }));
    },
    enabled: trimmed.length >= MIN_QUERY,
    placeholderData: keepPreviousData,
    // The vocabulary dict is deterministic, so a query's result is stable — cache it aggressively.
    staleTime: 5 * 60_000,
  });
};
